import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { api } from '../services/api';

const ROWS_PER_PAGE = 10;

const formatDate = (isoString) => {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ReportsTab = ({ refreshKey = 0 }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const filteredReports = reports.filter((report) => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const fileName = (report.requirements_file_name || '').toLowerCase();
      if (!fileName.includes(query)) return false;
    }

    if (startDate || endDate) {
      const completedAt = report.completed_at ? new Date(report.completed_at) : null;
      if (!completedAt) return false;
      if (startDate) {
        const [sy, sm, sd] = startDate.split('-').map(Number);
        const startLocal = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
        if (completedAt < startLocal) return false;
      }
      if (endDate) {
        const [ey, em, ed] = endDate.split('-').map(Number);
        const endLocal = new Date(ey, em - 1, ed, 23, 59, 59, 999);
        if (completedAt > endLocal) return false;
      }
    }

    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredReports.length / ROWS_PER_PAGE));
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getReports();
      setReports(response.data);
      setCurrentPage(1);
    } catch (err) {
      setError(err.userMessage || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports, refreshKey]);

  const handleDownload = async (jobId) => {
    try {
      setError(null);
      setDownloadingJobId(jobId);
      const response = await api.downloadJobResults(jobId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      const fileName = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `results_${jobId.slice(0, 8)}.zip`;

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.userMessage || 'Failed to download report');
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5" />
              Generated Reports
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              Reports from completed analysis jobs
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchReports}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-lg transition-all shadow-lg shadow-primary-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:from-neutral-400 disabled:to-neutral-500"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </motion.button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          {/* File name search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by requirements file name..."
              className="w-full pl-9 pr-9 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white text-sm placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Date range */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 whitespace-nowrap">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="py-2 px-3 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
            />
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 whitespace-nowrap">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              className="py-2 px-3 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
            />
            {(startDate || endDate) && (
              <button
                onClick={() => { handleStartDateChange(''); handleEndDateChange(''); }}
                className="p-1.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                title="Clear dates"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-400 text-sm">
            {error}
          </div>
        )}

        {loading && reports.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="w-6 h-6 animate-spin text-primary-500" />
            <span className="ml-2 text-neutral-600 dark:text-neutral-400">Loading reports...</span>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
            <DocumentTextIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No reports yet</p>
            <p className="text-sm mt-1">Reports will appear here after jobs complete successfully.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-700">
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">Job ID</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">Requirements File</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">Standard</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">Requirements</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">Date Generated</th>
                  <th className="text-center py-3 px-4 font-semibold text-neutral-700 dark:text-neutral-300">Download Report</th>
                </tr>
              </thead>
              <tbody>
                {paginatedReports.map((report) => (
                  <tr
                    key={report.job_id}
                    className="border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
                  >
                    <td className="py-3 px-4 font-mono text-xs text-neutral-600 dark:text-neutral-400">
                      {report.job_id.slice(0, 8)}...
                    </td>
                    <td className="py-3 px-4 text-neutral-900 dark:text-white font-medium truncate max-w-[250px]">
                      {report.requirements_file_name}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                        {report.file_standard}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-neutral-600 dark:text-neutral-400">
                      {report.total_requirements}
                    </td>
                    <td className="py-3 px-4 text-neutral-600 dark:text-neutral-400">
                      {formatDate(report.completed_at)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleDownload(report.job_id)}
                        disabled={downloadingJobId === report.job_id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors disabled:opacity-50"
                      >
                        {downloadingJobId === report.job_id ? (
                          <ArrowPathIcon className="w-4 h-4 animate-spin" />
                        ) : (
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        )}
                        {downloadingJobId === report.job_id ? 'Downloading...' : 'Download'}
                      </motion.button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {filteredReports.length > ROWS_PER_PAGE && (
              <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700 mt-4">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Showing {(currentPage - 1) * ROWS_PER_PAGE + 1}–{Math.min(currentPage * ROWS_PER_PAGE, filteredReports.length)} of {filteredReports.length} reports
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4 text-neutral-700 dark:text-neutral-300" />
                  </button>
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRightIcon className="w-4 h-4 text-neutral-700 dark:text-neutral-300" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsTab;
